/**
 * Web Vitals Monitoring
 *
 * Tracks Core Web Vitals (CLS, FID, LCP, TTFB, INP) for performance monitoring.
 * Integrates with the performance monitoring system and optionally reports to Sentry.
 *
 * @see https://web.dev/vitals/
 */

import { QuickLoggerFactory } from '@/lib/universal-logger';
import { isProduction } from '@/config';

const logger = QuickLoggerFactory.performance();

/** Web Vitals metric types */
export type WebVitalName = 'CLS' | 'FID' | 'LCP' | 'TTFB' | 'INP' | 'FCP';

/** Web Vital metric data */
export interface WebVitalMetric {
  name: WebVitalName;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType?: string;
}

/** Thresholds for rating metrics (from web.dev) */
const THRESHOLDS: Record<WebVitalName, { good: number; poor: number }> = {
  CLS: { good: 0.1, poor: 0.25 },
  FID: { good: 100, poor: 300 },
  LCP: { good: 2500, poor: 4000 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 },
  FCP: { good: 1800, poor: 3000 },
};

/** Storage for collected metrics */
const collectedMetrics: Map<WebVitalName, WebVitalMetric> = new Map();

/** Callbacks for metric updates */
const metricCallbacks: Set<(metric: WebVitalMetric) => void> = new Set();

/**
 * Rate a metric value as good, needs-improvement, or poor
 */
function rateMetric(name: WebVitalName, value: number): WebVitalMetric['rating'] {
  const threshold = THRESHOLDS[name];
  if (!threshold) return 'needs-improvement';

  if (value <= threshold.good) return 'good';
  if (value >= threshold.poor) return 'poor';
  return 'needs-improvement';
}

/**
 * Generate a unique ID for a metric
 */
function generateMetricId(): string {
  return `v1-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Report a metric to all registered callbacks
 */
function reportMetric(metric: WebVitalMetric): void {
  // Store the metric
  collectedMetrics.set(metric.name, metric);

  // Log the metric
  const logLevel = metric.rating === 'poor' ? 'warn' : 'info';
  logger[logLevel](`Web Vital: ${metric.name}`, {
    value: metric.value,
    rating: metric.rating,
    delta: metric.delta,
    id: metric.id,
  });

  // Notify all callbacks
  metricCallbacks.forEach((callback) => {
    try {
      callback(metric);
    } catch (error) {
      logger.error('Error in Web Vitals callback', { error });
    }
  });
}

/**
 * Observe Largest Contentful Paint (LCP)
 * Measures loading performance - the time it takes for the largest content element to render
 */
function observeLCP(): void {
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];

      if (lastEntry) {
        const value = lastEntry.startTime;
        reportMetric({
          name: 'LCP',
          value,
          rating: rateMetric('LCP', value),
          delta: value,
          id: generateMetricId(),
        });
      }
    });

    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (error) {
    logger.debug('LCP observation not supported', { error });
  }
}

/**
 * Observe First Input Delay (FID)
 * Measures interactivity - the time from first user interaction to browser response
 */
function observeFID(): void {
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const firstEntry = entries[0] as PerformanceEventTiming | undefined;

      if (firstEntry && 'processingStart' in firstEntry) {
        const value = firstEntry.processingStart - firstEntry.startTime;
        reportMetric({
          name: 'FID',
          value,
          rating: rateMetric('FID', value),
          delta: value,
          id: generateMetricId(),
        });
      }
    });

    observer.observe({ type: 'first-input', buffered: true });
  } catch (error) {
    logger.debug('FID observation not supported', { error });
  }
}

/**
 * Observe Cumulative Layout Shift (CLS)
 * Measures visual stability - the sum of all unexpected layout shifts
 */
function observeCLS(): void {
  if (typeof PerformanceObserver === 'undefined') return;

  let clsValue = 0;
  let clsEntries: PerformanceEntry[] = [];

  try {
    const observer = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        // Only count layout shifts without recent input
        const layoutShiftEntry = entry as PerformanceEntry & {
          hadRecentInput?: boolean;
          value?: number;
        };

        if (!layoutShiftEntry.hadRecentInput && layoutShiftEntry.value) {
          clsEntries.push(entry);
          clsValue += layoutShiftEntry.value;
        }
      }

      reportMetric({
        name: 'CLS',
        value: clsValue,
        rating: rateMetric('CLS', clsValue),
        delta: clsValue,
        id: generateMetricId(),
      });
    });

    observer.observe({ type: 'layout-shift', buffered: true });
  } catch (error) {
    logger.debug('CLS observation not supported', { error });
  }
}

/**
 * Observe Time to First Byte (TTFB)
 * Measures server responsiveness - the time from navigation start to first byte received
 */
function observeTTFB(): void {
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const navigationEntry = entries[0] as PerformanceNavigationTiming | undefined;

      if (navigationEntry && navigationEntry.responseStart) {
        const value = navigationEntry.responseStart;
        reportMetric({
          name: 'TTFB',
          value,
          rating: rateMetric('TTFB', value),
          delta: value,
          id: generateMetricId(),
          navigationType: navigationEntry.type,
        });
      }
    });

    observer.observe({ type: 'navigation', buffered: true });
  } catch (error) {
    logger.debug('TTFB observation not supported', { error });
  }
}

/**
 * Observe Interaction to Next Paint (INP)
 * Measures responsiveness - the latency of all interactions throughout the page lifecycle
 */
function observeINP(): void {
  if (typeof PerformanceObserver === 'undefined') return;

  let maxINP = 0;

  try {
    const observer = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        const eventEntry = entry as PerformanceEventTiming;
        if (eventEntry.duration > maxINP) {
          maxINP = eventEntry.duration;

          reportMetric({
            name: 'INP',
            value: maxINP,
            rating: rateMetric('INP', maxINP),
            delta: maxINP,
            id: generateMetricId(),
          });
        }
      }
    });

    observer.observe({ type: 'event', buffered: true, durationThreshold: 40 });
  } catch (error) {
    logger.debug('INP observation not supported', { error });
  }
}

/**
 * Observe First Contentful Paint (FCP)
 * Measures perceived load speed - the time from navigation to first content render
 */
function observeFCP(): void {
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const fcpEntry = entries.find((e) => e.name === 'first-contentful-paint');

      if (fcpEntry) {
        const value = fcpEntry.startTime;
        reportMetric({
          name: 'FCP',
          value,
          rating: rateMetric('FCP', value),
          delta: value,
          id: generateMetricId(),
        });
      }
    });

    observer.observe({ type: 'paint', buffered: true });
  } catch (error) {
    logger.debug('FCP observation not supported', { error });
  }
}

/**
 * Initialize Web Vitals monitoring
 * Call this once when the app starts
 */
export function initWebVitals(): void {
  if (typeof window === 'undefined') return;

  logger.info('Initializing Web Vitals monitoring');

  // Start observing all metrics
  observeLCP();
  observeFID();
  observeCLS();
  observeTTFB();
  observeINP();
  observeFCP();
}

/**
 * Subscribe to Web Vitals updates
 * @param callback Function to call when a metric is reported
 * @returns Unsubscribe function
 */
export function onWebVital(callback: (metric: WebVitalMetric) => void): () => void {
  metricCallbacks.add(callback);
  return () => metricCallbacks.delete(callback);
}

/**
 * Get all collected metrics
 */
export function getWebVitals(): Map<WebVitalName, WebVitalMetric> {
  return new Map(collectedMetrics);
}

/**
 * Get a specific metric
 */
export function getWebVital(name: WebVitalName): WebVitalMetric | undefined {
  return collectedMetrics.get(name);
}

/**
 * Get a summary of all metrics for reporting
 */
export function getWebVitalsSummary(): Record<string, unknown> {
  const summary: Record<string, unknown> = {};

  collectedMetrics.forEach((metric, name) => {
    summary[name] = {
      value: metric.value,
      rating: metric.rating,
    };
  });

  return summary;
}

/**
 * Report Web Vitals to Sentry (if available)
 * Note: Requires @sentry/react to be installed for Sentry reporting
 */
export function reportToSentry(): void {
  if (!isProduction()) return;

  // Log metrics summary (always works)
  logger.info('Web Vitals summary', {
    metrics: getWebVitalsSummary(),
  });

  // Sentry reporting - uncomment if @sentry/react is installed:
  // import('@sentry/react').then((Sentry) => {
  //   collectedMetrics.forEach((metric) => {
  //     Sentry.setMeasurement(metric.name, metric.value, 'millisecond');
  //   });
  // }).catch(() => {});
}

export default {
  initWebVitals,
  onWebVital,
  getWebVitals,
  getWebVital,
  getWebVitalsSummary,
  reportToSentry,
};
